/**
 * useCompetition — the competition module's single hook, and now a facade.
 *
 * It is called exactly once, in `useMainApp`, and its result travels down the
 * tree as a `compHook` prop through twenty files. That is why this file stays:
 * ~170 call sites read keys off that object, and none of them move.
 *
 * What changed is behind it. The hook was 1850 lines with five unrelated jobs
 * in one closure, so a listener fix and a registration fix touched the same
 * file, and the only way to know what a callback could reach was to read all
 * of it. Now each job is its own module, and what it needs is its parameter
 * list:
 *
 *   useCompetitionData      every listener, and everything derived from them
 *   useCompetitionAdmin     the writes that define a competition and close it
 *   useCompetitionSessions  running a session on the day (timer, finals phase)
 *   useCompetitionRegistry  who is entered, in what role, and who pays
 *   useCompetitionJudging   recording a climb, and taking it back
 *   useCompetitionActor     the `lastModifiedBy` stamp all of them carry
 *
 * Two things stay here because they are the seams the split runs through.
 * `activeCompId` is public state — callers set it, and both the detail
 * listeners and the ranking functions key on it. And `writeSession`, the
 * session-doc write primitive, is threaded from the sessions hook into the
 * admin hook: `setSessionAttemptsLocked` is the one caller outside its own
 * module, and it belongs beside its competition-level twin rather than beside
 * the primitive it happens to use.
 *
 * The return is listed key by key, never spread. `competitionContract.test.js`
 * compares it against every `compHook.*` read in `src/`, in both directions,
 * and a spread would hide exactly the thing that check exists to see.
 */
import { useState } from "react";
import { useGym } from "../contexts/GymContext";
import { usePermissions } from "../permissions";
import { useCompetitionActor } from "./competition/useCompetitionActor";
import { useCompetitionData } from "./competition/useCompetitionData";
import { useCompetitionSessions } from "./competition/useCompetitionSessions";
import { useCompetitionAdmin } from "./competition/useCompetitionAdmin";
import { useCompetitionRegistry } from "./competition/useCompetitionRegistry";
import { useCompetitionJudging } from "./competition/useCompetitionJudging";

export { unwrapCallableError } from "./competition/useCompetitionRegistry";

export function useCompetition(authUser) {
  const { gymId, gymMeta } = useGym();

  // Public: callers set it, and it selects which competition's groups,
  // sessions, registrations and attempts are listened to.
  const [activeCompId, setActiveCompId] = useState(null);

  const { withActor, deleteAttributed } = useCompetitionActor(authUser);

  // Whether the competition list may carry drafts. The rules let only the
  // gym-wide staff list a draft — `competitions.manage` or the platform
  // admin — and `can()` resolves exactly those two (adminAny grants every gym
  // cap). Anyone else MUST list with the status filter, or the whole query is
  // refused (docs/spec/05-competitions-core.md, "Who can read a draft").
  const { can } = usePermissions();
  const listDrafts = can("competitions.manage");

  const {
    competitions, loading, loadFailed, retryLoad,
    groups, sessions, registrations, detailStatus, attempts,
    myRegistration, myRegistrationMap, myProxyGuests,
    activeCompSessions, activeCompGroups, activeCompMyAttempts,
    setAttemptsScope,
    getRankings, generateFinalists,
  } = useCompetitionData({ authUser, gymId, activeCompId, listDrafts });

  const {
    writeSession,
    startSession, pauseSession, resumeSession, finishSession, restartSession, adjustRemainingSeconds,
    startFinalsObservation, startFinalsClimbing, startFinalsRest, finishFinals,
  } = useCompetitionSessions({ gymId, sessions, withActor });

  const {
    createCompetition, updateCompetition, deleteCompetition,
    saveGroup, deleteGroup, setCompetitionDenorm,
    saveSession, deleteSession,
    activateCompetition, closeCompetition, syncCompetitionClimbs,
    setCompetitionAttemptsLocked, setSessionAttemptsLocked,
    setChallengeParticipation, setChallengeExclusion, setChallengeRouteExclusions, closeChallenge,
  } = useCompetitionAdmin({ gymId, gymMeta, authUser, competitions, withActor, deleteAttributed, writeSession });

  const {
    registerCompetitor, registerJudge, registerOrganizer, unregister,
    updateRegistration, editRegistration, updateEntryFeeStatus, updatePaymentMethod,
    registerGuestCompetitor, updateGuestCompetitor, unregisterGuest,
    saveParentBilling, saveBillingToProfile,
    loadBillingExportData, loadRegistrationContact,
    backfillStartNumbers,
  } = useCompetitionRegistry({ gymId, authUser, withActor, deleteAttributed });

  const {
    saveAttempt, undoJamSave, recordJudgeEvent, undoJudgeEvent,
  } = useCompetitionJudging({ gymId, authUser, competitions, sessions });

  return {
    // Data
    competitions, loading, loadFailed, retryLoad,
    groups, sessions, registrations, attempts,
    myRegistration, myRegistrationMap, myProxyGuests,
    activeCompSessions, activeCompGroups, activeCompMyAttempts,
    // "idle" | "loading" | "ready" | "failed" — whether the groups, sessions and
    // registrations above are THIS `activeCompId`'s (see useCompetitionData)
    detailStatus,
    // Active comp selection
    activeCompId, setActiveCompId,
    // Attempts listener scope — see the state declaration in useCompetitionData
    setAttemptsScope,
    // CRUD
    createCompetition, updateCompetition, deleteCompetition,
    saveGroup, deleteGroup, setCompetitionDenorm,
    saveSession, deleteSession,
    backfillStartNumbers,
    // Session control
    startSession, pauseSession, resumeSession, finishSession, restartSession, adjustRemainingSeconds,
    // Finals session control
    startFinalsObservation, startFinalsClimbing, startFinalsRest, finishFinals, generateFinalists,
    // Registration
    registerCompetitor, registerJudge, registerOrganizer, unregister, updateRegistration, editRegistration, updateEntryFeeStatus, updatePaymentMethod,
    // Guest competitors
    registerGuestCompetitor, updateGuestCompetitor, unregisterGuest,
    saveParentBilling, saveBillingToProfile,
    loadBillingExportData, loadRegistrationContact,
    // Attempts
    saveAttempt, undoJamSave, recordJudgeEvent, undoJudgeEvent,
    // Lifecycle
    activateCompetition, closeCompetition, syncCompetitionClimbs,
    setCompetitionAttemptsLocked, setSessionAttemptsLocked,
    // Challenge
    setChallengeParticipation, setChallengeExclusion, setChallengeRouteExclusions, closeChallenge,
    // Ranking
    getRankings,
  };
}
